import { Box, HStack, Icon, Stack, Tag, Text } from "@chakra-ui/react";
import LineLimiter from "@raiden/library/components/LineLimit";
import {
  PRESTATIONS_HOLDER_TYPES,
  PRESTATIONS_REGISTRATION_MODES,
} from "@raiden/library/constants/prestations";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import generatePampaUri from "@raiden/library/libraries/utils/generatePampaUri";
import capitalize from "@splitfire-agency/raiden-library/dist/libraries/utils/capitalize";
import NextLink from "next/link";
import { MdOutlineCalendarToday, MdOutlinePeople } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";
import { getPrestationSlug } from "../../../helpers/prestations/getPrestationSlug";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Prestation").Prestation} prestation
 * @property {boolean} [isDisabled]
 */
/**
 * @param {Props} props
 */
function Prestation({ prestation, isDisabled = false }) {
  const intl = useIntl();

  return (
    <NextLink
      href={generateFrontPath({
        id: "@prestations.view",
        parameters: {
          prestationSlug: getPrestationSlug({ prestation }),
        },
      })}
      passHref>
      <Box position="relative">
        <Stack
          as="a"
          spacing="0"
          justifyContent="space-between"
          h="full"
          borderRadius="8px"
          opacity={isDisabled ? 0.5 : 1}
          cursor="pointer"
          _hover={{
            backgroundColor: "gray.50",
          }}>
          <Box
            display="block"
            position="relative"
            borderRadius="8px"
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: prestation.post?.current_revision?.cover
                ? generatePampaUri({
                    image: prestation.post.current_revision.cover,
                    mode: "crop",
                    width: 650,
                  })
                : generateFrontPath({
                    id: "internal-assets",
                    parameters: {
                      filePath: "placeholder-subscription.svg",
                    },
                    includeBasePath: true,
                  }),
              backgroundPosition: "center",
              backgroundSize: "cover",
            }}>
            <Stack
              position="relative"
              spacing="0.25rem"
              justify="flex-end"
              height="16.25rem"
              p="1rem"
              background="linear-gradient(180deg, transparent 0%, transparent 50%, rgba(0,0,0,0.5) 100%)">
              <Box>
                <Tag
                  variant="solid"
                  size="md"
                  colorScheme="brandPrimary"
                  borderRadius="full">
                  {prestation.category?.name}
                </Tag>
              </Box>
            </Stack>
          </Box>

          <Stack
            flexGrow={1}
            spacing="0.375rem"
            justifyContent="space-between"
            p="1rem">
            <Stack spacing="0.375rem">
              <Text fontSize="1.25rem" fontWeight="400" lineHeight="1.75rem">
                {prestation.name}
              </Text>

              {prestation.infos !== null && (
                <LineLimiter limit={2}>
                  <Text
                    whiteSpace="pre"
                    fontSize="0.875rem"
                    fontWeight={400}
                    lineHeight="1.25rem">
                    {prestation.infos}
                  </Text>
                </LineLimiter>
              )}
            </Stack>

            <HStack spacing="1rem">
              <Box w="50%">
                <HStack spacing="0.75rem">
                  <Icon
                    as={MdOutlinePeople}
                    boxSize="24px"
                    color="brandPrimary.300"
                  />

                  <Text>
                    {prestation.holder_types.length > 0 ? (
                      <>
                        {capitalize(
                          intl.formatList(
                            prestation.holder_types.map((holderType) => {
                              const message =
                                PRESTATIONS_HOLDER_TYPES[holderType]
                                  ?.labelPlural;
                              return message
                                ? intl.formatMessage(message)
                                : holderType;
                            }),
                            { type: "conjunction" },
                          ),
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </Text>
                </HStack>
              </Box>

              <Box w="50%">
                <HStack spacing="0.75rem">
                  <Icon
                    as={MdOutlineCalendarToday}
                    boxSize="24px"
                    color="brandPrimary.300"
                  />

                  <Text>
                    {PRESTATIONS_REGISTRATION_MODES[
                      prestation.registration_mode
                    ]?.label
                      ? intl.formatMessage(
                          PRESTATIONS_REGISTRATION_MODES[
                            prestation.registration_mode
                          ]?.label,
                        )
                      : prestation.registration_mode}
                  </Text>
                </HStack>
              </Box>
            </HStack>
          </Stack>
        </Stack>

        {isDisabled && (
          <Box pointerEvents="none" position="absolute" top="1rem" left="1rem">
            <Tag colorScheme="orange">
              <FormattedMessage defaultMessage="Activité terminée" />
            </Tag>
          </Box>
        )}
      </Box>
    </NextLink>
  );
}

export default Prestation;
